import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";

import api from "../api/axios";
import { setAccessToken, markHasRefresh } from "../api/axios";
import { useAuth, AuthUser } from "../context/AuthContext";
import "../styles/LoginPage.css";

interface DecodedJWT {
  sub: number;
  email: string;
  rol: AuthUser["roles"]; // compat antiguo
  roles?: AuthUser["roles"]; // payload nuevo
  exp: number;
}

/* helpers */
function normalizeRoles(r: unknown): AuthUser["roles"] {
  if (Array.isArray(r)) return r as AuthUser["roles"];
  if (typeof r === "string" && r) return [r] as AuthUser["roles"];
  return [];
}

function extractErrorMessage(err: any, t: (k: string) => string) {
  const data = err?.response?.data ?? err;
  if (typeof data === "string" && data.trim()) return data;

  const msg = data?.message;
  if (typeof msg === "string" && msg.trim()) return msg;
  if (Array.isArray(msg) && msg.length) return String(msg[0]);

  const statusText = err?.response?.statusText;
  if (typeof statusText === "string" && statusText.trim()) return statusText;

  const fb = t("common.invalidCreds");
  return typeof fb === "string" && fb.trim() ? fb : "Usuario o contraseña incorrectos";
}

export default function LoginPage() {
  const { t } = useTranslation<"translation">();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const res = await api.post("/auth/login", { email, password }, { withCredentials: true });
      const data = res?.data as any;
      const { access_token, user: backendUser } = data ?? {};

      if (!access_token) {
        setError(extractErrorMessage(data, t));
        return;
      }

      let authUser: AuthUser;
      if (backendUser) {
        const roles = normalizeRoles(backendUser.roles ?? backendUser.role);
        authUser = {
          id: backendUser.id,
          email: backendUser.email,
          roles,
          mustChangePwd: !!backendUser.mustChangePwd,
          // ⚠️ no guardes access_token dentro del user del contexto
        };
      } else {
        const { sub, email: em, rol, roles } = jwtDecode<DecodedJWT>(access_token);
        authUser = {
          id: sub,
          email: em,
          roles: normalizeRoles(roles ?? rol),
          mustChangePwd: false,
          // ⚠️ no guardes access_token dentro del user del contexto
        };
      }

      // ⬇️ Guardamos el access token SOLO en memoria para Axios
      setAccessToken(access_token);

      // ⬇️ Marcamos que hay refresh cookie presente (para re-hidratar tras reload)
      markHasRefresh();

      // ⬇️ Guardamos el usuario en el contexto (sin token)
      setUser(authUser);

      navigate(authUser.mustChangePwd ? "/change-password" : "/", {
        replace: true,
      });
    } catch (err: any) {
      setError(extractErrorMessage(err, t));
    }
  };

  return (
    <div className="login-container">
      {error && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: "fixed",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "10px 14px",
            background: "#b00020",
            color: "#a05c5cff",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            zIndex: 9999,
            fontWeight: 600,
            maxWidth: "90%",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      <div className="image-container" />
      <div className="form-container">
        <form onSubmit={submit} className="login-form" noValidate>
          <div className="logo-wrapper" />
          <h2 className="title">{t("login.title")}</h2>

          {error ? (
            <p className="error-message" role="alert" aria-live="assertive">
              {error}
            </p>
          ) : null}

          <input
            className="input-field"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            inputMode="email"
            type="email"
            required
          />

          <input
            className="input-field"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          <div className="forgot-password-wrapper">
            <Link to="/forgot-password" className="forgot-password-link">
              {t("login.forgot")}
            </Link>
          </div>

          <button type="submit" className="submit-button">
            {t("login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
